"use client"

import { useState } from "react"

import Image, { type ImageProps } from "next/image"

import { focalPoint } from "@/platform/images/focal-points"

const TILE_BLUR_DATA_URL =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSc0MCcgaGVpZ2h0PSc0MCc+PHJlY3Qgd2lkdGg9JzQwJyBoZWlnaHQ9JzQwJyBmaWxsPScjZWNlZWYwJy8+PC9zdmc+"

export const PRODUCT_PHOTO_QUALITY = 90

const Placeholder = ({ alt, fill }: { alt: string; fill?: boolean }) => (
  <div role="img" aria-label={alt} className={`bg-surface-newgray ${fill ? "absolute inset-0" : "aspect-square w-full"}`} />
)

// Products without a thumbnail arrive as src="" — the browser treats that as a
// broken image, so hold the placeholder instead. A live src can also 404 (dead
// seller CDN entries are common), and an <img> that fails paints the browser's
// broken glyph plus the alt text over the card, so failures fall back too.
export const TileImage = ({ alt, src, style, fetchPriority, priority, preload, quality = PRODUCT_PHOTO_QUALITY, ...props }: ImageProps) => {
  const [failedSrc, setFailedSrc] = useState<ImageProps["src"]>()
  if (!src || failedSrc === src) return <Placeholder alt={alt} fill={props.fill} />
  // A crop is authored when the picture carries a registered focal point or the
  // caller states one. `50% 50%` written inline is the default, not a decision.
  const objectPosition = style?.objectPosition ?? focalPoint(src)
  const authored = objectPosition !== undefined && objectPosition !== "50% 50%" ? true : focalPoint(src) !== undefined
  // Next 16 split the old priority contract into `preload` plus an explicit
  // fetchpriority hint; without the hint the LCP image goes out at network
  // priority Low. Callers keep the familiar `priority`, translated here.
  const eager = preload || priority
  return (
    <Image
      alt={alt}
      src={src}
      placeholder="blur"
      blurDataURL={TILE_BLUR_DATA_URL}
      quality={quality}
      preload={eager}
      fetchPriority={fetchPriority ?? (eager ? "high" : undefined)}
      onError={() => setFailedSrc(src)}
      data-focal={authored ? "authored" : undefined}
      style={objectPosition ? { ...style, objectPosition } : style}
      {...props}
    />
  )
}
